import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Bootmodel from "../common/Bootmodel.css";
import { chatbotSubmit } from "../services/commonService";
import { useSelector } from "react-redux";
import AuthModal from "../auth/AuthModal";
import jsPDF from "jspdf";

function BotModal() {
  const { user } = useSelector((state) => state.auth);
  const [showUserLogin, setShowUserLogin] = useState(false);

  const [formData, setFormData] = useState({
    fromDestination: "",
    toDestination: "",
    noOfAdults: "1",
    noOfChildren: "0",
    cabNeed: "Yes",
    purposeType: "Tourism",
    mood: "",
    budget: "",
    startDate: "",
    endDate: "",
  });

  const [errors, setErrors] = useState({});
  const [chatbotReply, setChatbotReply] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    // if (!formData.fromDestination.trim() && !formData.toDestination.trim()) {
    //   newErrors.fromDestination = "Enter at least one location";
    //   newErrors.toDestination = "Enter at least one location";
    // }
    if (!formData.fromDestination.trim()) {
      newErrors.fromDestination = "Enter at least From location";
    }
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        newErrors.endDate = "End date must be after start date";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitWithChatbot = async () => {
    if (!user) { setShowUserLogin(true); return; }
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      setChatbotReply("");
      const payload = {
        // fromDestination: formData.fromDestination,
        // toDestination: formData.toDestination,
        fromDestination: formData.fromDestination,
        ...(formData.toDestination && { toDestination: formData.toDestination }),
        noOfAdults: formData.noOfAdults,
        noOfChildren: formData.noOfChildren,
        purposeType: formData.purposeType,
        mood: formData.mood,
        budget: formData.budget,
        startDate: formData.startDate,
        endDate: formData.endDate,
      };
      const response = await chatbotSubmit(payload);
      setChatbotReply(response.result);
      setTimeout(() => {
        document.querySelector(".ai-response")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (error) {
      console.error("Error generating trip plan:", error);
      setChatbotReply(`Error: ${error.message || "Failed to generate trip plan. Please try again."}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getTripDuration = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return days > 0 ? days : 0;
    }
    return 0;
  };

  const tripDays = getTripDuration();

  const extractLinks = (text) => {
    const links = { flight: "", hotel: "", bus: "", guide: "" };
    const matches = text.match(/https?:\/\/[^\s]+/g) || [];
    matches.forEach((url) => {
      if (url.includes("/flights")) links.flight = url;
      else if (url.includes("/hotel")) links.hotel = url;
      else if (url.includes("/bus")) links.bus = url;
      else if (url.includes("/guides")) links.guide = url;
    });
    return links;
  };

  const links = extractLinks(chatbotReply);

  // ── Clean destination string (remove special chars) ──
  const cleanDest = (str) => (str || "").replace(/[^\w\s]/g, "").trim();

  const downloadAsPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    let y = 0;

    // ── Colors ──────────────────────────────────────────
    const brown = [122, 96, 64];
    const gold = [200, 169, 110];
    const darkText = [35, 35, 35];
    const lightText = [100, 90, 80];
    const white = [255, 255, 255];
    const pageBg = [252, 249, 245];
    const cardBg = [255, 253, 250];

    // ── Helpers ──────────────────────────────────────────
    const checkPage = (needed = 10) => {
      if (y + needed > pageHeight - 16) {
        drawFooter();
        pdf.addPage();
        drawPageBg();
        y = 24;
      }
    };

    const drawPageBg = () => {
      pdf.setFillColor(...pageBg);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");
      pdf.setFillColor(...gold);
      pdf.rect(0, 0, 3, pageHeight, "F");
    };

    const drawFooter = () => {
      pdf.setFillColor(...brown);
      pdf.rect(0, pageHeight - 12, pageWidth, 12, "F");
      pdf.setFontSize(8);
      pdf.setTextColor(...white);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        "SAFARIX  |  Your Travel Companion  |  Powered by ARIX",
        pageWidth / 2, pageHeight - 5, { align: "center" }
      );
    };

    const drawSectionHeading = (text) => {
      checkPage(16);
      y += 6;
      pdf.setFillColor(...gold);
      pdf.rect(margin, y, 3, 8, "F");
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...brown);
      pdf.text(text.toUpperCase(), margin + 7, y + 6);
      pdf.setDrawColor(...gold);
      pdf.setLineWidth(0.4);
      pdf.line(margin + 7, y + 8, pageWidth - margin, y + 8);
      y += 14;
    };

    const drawDayHeading = (text) => {
      checkPage(14);
      y += 4;
      pdf.setFillColor(...brown);
      pdf.roundedRect(margin, y, contentWidth, 10, 3, 3, "F");
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...white);
      pdf.text(text, margin + 6, y + 7);
      y += 15;
    };

    const drawBullet = (text) => {
      const lines = pdf.splitTextToSize(text, contentWidth - 12);
      checkPage(lines.length * 6 + 4);
      pdf.setFillColor(...gold);
      pdf.circle(margin + 3, y + 2.5, 1.2, "F");
      pdf.setFontSize(9.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkText);
      pdf.text(lines, margin + 8, y + 3.5);
      y += lines.length * 6 + 2;
    };

    const drawBodyText = (text) => {
      const lines = pdf.splitTextToSize(text, contentWidth);
      checkPage(lines.length * 6 + 2);
      pdf.setFontSize(9.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkText);
      pdf.text(lines, margin, y);
      y += lines.length * 6 + 3;
    };

    // ── PAGE 1: COVER ────────────────────────────────────
    drawPageBg();

    // Header bar
    pdf.setFillColor(...brown);
    pdf.rect(0, 0, pageWidth, 52, "F");
    pdf.setFillColor(...gold);
    pdf.rect(0, 52, pageWidth, 2, "F");

    // Logo
    pdf.setFontSize(28);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...white);
    pdf.text("SAFARIX", pageWidth / 2, 22, { align: "center" });

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(220, 200, 170);
    pdf.text("Your AI-Powered Travel Companion", pageWidth / 2, 32, { align: "center" });

    pdf.setFontSize(8);
    pdf.setTextColor(180, 160, 130);
    pdf.text("Powered by ARIX", pageWidth / 2, 42, { align: "center" });

    // Trip card
    y = 70;
    pdf.setFillColor(...cardBg);
    pdf.setDrawColor(...gold);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, y, contentWidth, 68, 4, 4, "FD");

    // TRIP SUMMARY label
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...gold);
    pdf.text("TRIP SUMMARY", pageWidth / 2, y + 10, { align: "center" });

    // FIX 1: Clean destination - no special chars
    const fromClean = cleanDest(formData.fromDestination) || "Origin";
    // const toClean = cleanDest(formData.toDestination) || "Destination";
    const toClean = cleanDest(formData.toDestination);
    const tripTitle = toClean
      ? `${fromClean} -> ${toClean}`
      : `${fromClean}`;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...brown);
    pdf.text(tripTitle, pageWidth / 2, y + 24, { align: "center" });

    // Divider
    pdf.setDrawColor(...gold);
    pdf.setLineWidth(0.3);
    pdf.line(margin + 20, y + 30, pageWidth - margin - 20, y + 30);

    // FIX 2: Details row — ek baar, sirf ek pdf.text call per column
    const budgetLabel = formData.budget
      ? `Rs. ${parseInt(formData.budget).toLocaleString("en-IN")}`
      : "N/A";

    const details = [
      `${tripDays > 0 ? tripDays : "-"} Days`,
      `${formData.noOfAdults} Adult${formData.noOfAdults > 1 ? "s" : ""}${parseInt(formData.noOfChildren) > 0 ? `, ${formData.noOfChildren} Kids` : ""}`,
      budgetLabel,
      formData.purposeType || "Tourism",
    ];

    const detailLabels = ["Duration", "Travellers", "Budget", "Type"];
    const colW = contentWidth / details.length;

    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...gold);
    detailLabels.forEach((label, i) => {
      const cx = margin + colW * i + colW / 2;
      pdf.text(label.toUpperCase(), cx, y + 40, { align: "center" });
    });

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...brown);
    details.forEach((val, i) => {
      const cx = margin + colW * i + colW / 2;
      // FIX 2: Only ONE pdf.text call per detail
      pdf.text(val, cx, y + 50, { align: "center" });
    });

    // Date range
    if (formData.startDate && formData.endDate) {
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...lightText);
      pdf.text(
        `${formData.startDate}   to   ${formData.endDate}`,
        pageWidth / 2, y + 62, { align: "center" }
      );
    }

    // Gold strip
    y = 155;
    pdf.setFillColor(...gold);
    pdf.rect(margin, y, contentWidth, 0.5, "F");

    // Generated on
    y = 272;
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...lightText);
    pdf.text(
      `Generated on: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}`,
      pageWidth / 2, y, { align: "center" }
    );

    drawFooter();

    // ── PAGE 2+: ITINERARY ───────────────────────────────
    pdf.addPage();
    drawPageBg();
    y = 20;

    const cleanText = chatbotReply
      .replace(/https?:\/\/[^\s]+/g, "")
      .replace(/\b(Flights|Hotels|Bus|Guides)\s*:\s*/gi, "")
      .replace(/\/\/\s*/g, "")
      .replace(/Explore booking options[\s\S]*$/i, "")
      .replace(/\*\*(.*?)\*\*/g, "$1");

    const lines = cleanText.split("\n").filter((l) => l.trim());

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      if (/^Day \d+/i.test(trimmed)) {
        drawDayHeading(trimmed);
      } else if (/^[A-Z][A-Za-z\s]{3,40}:?$/.test(trimmed) && trimmed.length < 50) {
        drawSectionHeading(trimmed.replace(/:$/, ""));
      } else if (/^[-•◆]\s/.test(trimmed) || /^\d+-/.test(trimmed)) {
        drawBullet(trimmed.replace(/^[-•◆\d+\-]\s*/, ""));
      } else {
        drawBodyText(trimmed);
      }
    });

    drawFooter();

    // ── SAVE ─────────────────────────────────────────────
    const fileName = `SAFARIX_${toClean}_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.pdf`;
    pdf.save(fileName);
  };

  return (
    <div className="trip-container">
      <div className="trip-hero">
        <div className="container">
          <div className="row">
            <h1>Plan Your Dream Trip</h1>
            <p>Let's design a journey tailored just for you</p>
          </div>
        </div>
      </div>

      <div className="container trip-glass-card">
        <div className="row g-5">
          {/* LEFT */}
          <div className="col-lg-8">
            <h4 className="form-title">Travel Details</h4>

            <Form>
              <Row>
                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-geo-alt"></i>
                    <Form.Control
                      placeholder="From (Delhi)"
                      name="fromDestination"
                      value={formData.fromDestination}
                      onChange={handleChange}
                      isInvalid={!!errors.fromDestination}
                    />
                    {errors.fromDestination && (
                      <Form.Control.Feedback type="invalid">{errors.fromDestination}</Form.Control.Feedback>
                    )}
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-flag"></i>
                    <Form.Control
                      placeholder="To (Jaipur)"
                      name="toDestination"
                      value={formData.toDestination}
                      onChange={handleChange}
                      isInvalid={!!errors.toDestination}
                    />
                    {errors.toDestination && (
                      <Form.Control.Feedback type="invalid">{errors.toDestination}</Form.Control.Feedback>
                    )}
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-calendar-check"></i>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split("T")[0]}
                      isInvalid={!!errors.startDate}
                    />
                    {errors.startDate && (
                      <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
                    )}
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-calendar-x"></i>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      min={formData.startDate || new Date().toISOString().split("T")[0]}
                      isInvalid={!!errors.endDate}
                    />
                    {errors.endDate && (
                      <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
                    )}
                  </div>
                  {tripDays > 0 && (
                    <small className="text-muted ms-2">{tripDays} day{tripDays > 1 ? "s" : ""} trip</small>
                  )}
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-currency-rupee"></i>
                    <Form.Select name="budget" value={formData.budget} onChange={handleChange}>
                      <option value="">Select Budget Range</option>
                      <option value="5000">Under ₹5,000 (Budget)</option>
                      <option value="10000">₹5,000 - ₹10,000 (Economy)</option>
                      <option value="20000">₹10,000 - ₹20,000 (Standard)</option>
                      <option value="35000">₹20,000 - ₹35,000 (Comfort)</option>
                      <option value="50000">₹35,000 - ₹50,000 (Premium)</option>
                      <option value="75000">₹50,000 - ₹75,000 (Luxury)</option>
                      <option value="100000">Above ₹75,000 (Ultra Luxury)</option>
                    </Form.Select>
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-bullseye"></i>
                    <Form.Select name="purposeType" value={formData.purposeType} onChange={handleChange}>
                      <option value="Tourism">🏖️ Tourism</option>
                      <option value="Business">💼 Business</option>
                      <option value="Honeymoon">💑 Honeymoon</option>
                      <option value="Adventure">⛰️ Adventure</option>
                    </Form.Select>
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-person"></i>
                    <Form.Select name="noOfAdults" value={formData.noOfAdults} onChange={handleChange}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>{n} Adult{n > 1 ? "s" : ""}</option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>

                <Col xs={6} md={6} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-people"></i>
                    <Form.Select name="noOfChildren" value={formData.noOfChildren} onChange={handleChange}>
                      {[0, 1, 2, 3].map((n) => (
                        <option key={n} value={n}>{n} Children</option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>

                <Col md={12} className="mb-4">
                  <div className="input-group-modern">
                    <i className="bi bi-emoji-smile"></i>
                    <Form.Select name="mood" value={formData.mood} onChange={handleChange}>
                      <option value="">Select your mood (optional)</option>
                      <option value="Relaxed">😌 Relaxed</option>
                      <option value="Happy">😊 Happy</option>
                      <option value="Adventurous">🏔️ Adventurous</option>
                      <option value="Romantic">💕 Romantic</option>
                      <option value="Low or Tired">😔 Low or Tired</option>
                      <option value="Spiritual">🙏 Spiritual</option>
                    </Form.Select>
                  </div>
                </Col>
              </Row>
            </Form>

            <div className="text-center mt-2">
              <button
                className="explore-btn"
                onClick={handleSubmitWithChatbot}
                disabled={isLoading}
              >
                {isLoading ? "Planning..." : (
                  <>
                    <i className="bi bi-magic me-2"></i>Ask ARIX
                  </>
                )}
              </button>
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="ai-response mt-4">
                <div className="text-center py-5">

                  <p>ARIX is creating your personalized itinerary...</p>
                </div>
              </div>
            )}

            {/* AI Response */}
            {chatbotReply && !isLoading && (
              <div className="ai-response mt-4">
                <div className="d-md-flex justify-content-between align-items-center mb-3">
                  <h3>
                    <i className="bi bi-robot me-2"></i>
                    Your Travel Plan
                  </h3>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={downloadAsPDF}>
                      <i className="bi bi-file-earmark-pdf me-1"></i> PDF Download
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setChatbotReply("")}>
                      <i className="bi bi-x-circle"></i> Clear
                    </button>
                  </div>
                </div>

                <div className="ai-response-text">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: chatbotReply
                        .replace(/https?:\/\/[^\s]+/g, "")
                        .replace(/\b(Flights|Hotels|Bus|Guides)\s*:\s*/gi, "")
                        .replace(/\/\/\s*/g, "")
                        .replace(/Explore booking options[\s\S]*$/i, "")
                        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                        .replace(/^(Day \d+[:\-].*?)$/gm, '<div class="arix-day-heading">$1</div>')
                        .replace(/^([A-Z][A-Za-z\s]+):$/gm, '<div class="arix-section-heading">$1</div>')
                        .replace(/^[-•◆]\s*(.+)$/gm, '<div class="arix-bullet"><span class="arix-dot">◆</span><span>$1</span></div>')
                        .replace(/\n/g, "<br/>"),
                    }}
                  />

                  {(links.flight || links.hotel || links.bus) && (
                    <div className="booking-buttons mt-4 d-flex gap-3 flex-wrap">
                      {links.flight && (
                        <a href={links.flight} className="arix-book-btn arix-btn-flight" target="_blank" rel="noreferrer">
                          Flights
                        </a>
                      )}
                      {links.hotel && (
                        <a href={links.hotel} className="arix-book-btn arix-btn-flight" target="_blank" rel="noreferrer">
                          Hotels
                        </a>
                      )}
                      {links.bus && (
                        <a href={links.bus} className="arix-book-btn arix-btn-flight" target="_blank" rel="noreferrer">
                          Bus
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div className="col-lg-4">
            <div className="assistant-card">
              <h5><i className="bi bi-robot me-2"></i>ARIX Travel Assistant</h5>
              <p>Smart itinerary planning based on your preferences, time and budget.</p>
              <ul>
                <li><i className="bi bi-check-circle"></i> Day-by-day planning</li>
                <li><i className="bi bi-check-circle"></i> Budget optimization</li>
                <li><i className="bi bi-check-circle"></i> Mood-based suggestions</li>
                <li><i className="bi bi-check-circle"></i> Local food recommendations</li>
              </ul>

              {tripDays > 0 && formData.fromDestination && formData.toDestination && (
                <div className="trip-summary mt-4 p-3 bg-light rounded">
                  <h6>Trip Summary</h6>
                  <p className="mb-1">
                    <strong>{formData.fromDestination}</strong> → <strong>{formData.toDestination}</strong>
                  </p>
                  <p className="mb-1"><i className="bi bi-calendar3"></i> {tripDays} days</p>
                  {formData.budget && (
                    <p className="mb-1">
                      <i className="bi bi-currency-rupee"></i> ₹{formData.budget}
                      <span className="ms-2 badge bg-primary">
                        {formData.budget <= 10000 && "Budget"}
                        {formData.budget > 10000 && formData.budget <= 35000 && "Standard"}
                        {formData.budget > 35000 && formData.budget <= 50000 && "Premium"}
                        {formData.budget > 50000 && "Luxury"}
                      </span>
                    </p>
                  )}
                  <p className="mb-0">
                    <i className="bi bi-people"></i> {formData.noOfAdults} Adult{formData.noOfAdults > 1 ? "s" : ""}
                    {formData.noOfChildren > 0 && `, ${formData.noOfChildren} Child${formData.noOfChildren > 1 ? "ren" : ""}`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        show={showUserLogin}
        onClose={() => setShowUserLogin(false)}
        setShowUserLogin={setShowUserLogin}
      />
    </div>
  );
}

export default BotModal;